import React from 'react'
import { Bell } from 'lucide-react'
import { NavLink, useLocation } from 'react-router-dom'
import { ThemeToggle } from '@/shell/ThemeToggle'
import { modules } from '@/modules/registry'
import { routes } from '@/lib/routes'
import { QUIET_HOURS_LABEL, useQuietHours } from '@/lib/quiet-hours'
import { useKuroBoardOverview } from '@/modules/kuroboard/hooks/useKuroBoardOverview'

const dashboardModuleIds = ['kuroboard', 'kurotrader', 'goku-health', 'kurorunner']

export function TopNav() {
  const location = useLocation()
  const isDashboard = location.pathname.startsWith('/dashboard')
  const quietHours = useQuietHours()
  const { data: boardData } = useKuroBoardOverview()
  const unreadAlerts = boardData?.summary.unreadNotificationCount ?? 0

  return (
    <header
      className="fixed top-0 left-0 right-0 z-50 flex items-center h-12 px-6 bg-bg border-b border-border"
      style={{ height: 48 }}
      >
        {/* Home link */}
        <NavLink
          to={routes.root}
          className="shrink-0 mr-8 text-text hover:text-accent transition-colors"
        >
          <span className="text-lg leading-none">🐈‍⬛</span>
          <span
            className={`ml-2 text-small font-semibold tracking-tight ${!isDashboard ? 'text-text' : 'text-text-dim'}`}
          >
            home
          </span>
        </NavLink>

       {isDashboard ? (
         <nav className="flex items-center gap-0.5 flex-1" aria-label="Dashboard modules">
           {modules
             .filter((m) => dashboardModuleIds.includes(m.id))
            .map((mod) => {
              const Icon = mod.icon
              return (
                <NavLink
                  key={mod.id}
                  to={mod.basePath}
                  className={({ isActive }) =>
                    `flex items-center gap-1.5 px-3 py-1.5 rounded text-tiny transition-colors ${
                      isActive
                        ? 'text-text bg-surface'
                        : 'text-text-dim hover:text-text hover:bg-surface'
                    }`
                  }
                >
                  <Icon size={12} />
                  {mod.name}
                </NavLink>
              )
            })}
        </nav>
       ) : (
         <nav className="flex flex-1 items-center justify-end">
           {quietHours ? (
            <span className="text-tiny text-text-dim">Dashboard unavailable during {QUIET_HOURS_LABEL}</span>
          ) : (
            <NavLink
              to={routes.dashboard()}
              className="rounded border border-border px-3 py-1.5 text-tiny text-text-dim transition-colors hover:bg-surface hover:text-text"
            >
              Open dashboard
            </NavLink>
          )}
         </nav>
       )}

        <div className="ml-auto flex shrink-0 items-center gap-2">
         {isDashboard && (
           <NavLink
             to={routes.kuroboard.notifications()}
             className={({ isActive }) =>
               `relative inline-flex items-center justify-center rounded border px-2.5 py-1.5 transition-colors ${
                 isActive
                   ? 'border-accent bg-surface text-text'
                   : 'border-border text-text-dim hover:bg-surface hover:text-text'
               }`
             }
             aria-label={unreadAlerts > 0 ? `${unreadAlerts} unread alerts` : 'Dashboard alerts'}
             title={unreadAlerts > 0 ? `${unreadAlerts} unread alerts` : 'Dashboard alerts'}
           >
             <Bell size={14} />
             {unreadAlerts > 0 && (
               <span className="absolute -right-1.5 -top-1.5 min-w-[1.1rem] rounded-full bg-warning px-1 text-center text-[0.625rem] font-semibold leading-[1.1rem] text-bg">
                 {unreadAlerts > 99 ? '99+' : unreadAlerts}
               </span>
             )}
           </NavLink>
         )}

         {/* Theme toggle */}
         <ThemeToggle />
       </div>
     </header>
  )
}
